// The website's signage for the plan, matching the app's paywall (owner,
// 2026-10-25: "should our website match this signage in the app?" → the mock →
// "Build it"). Two pieces and a frame:
//   · MemberHero      — the 200+ barns in gold, the one claim, the barns on the
//                       board right now (real figures, src/lib/memberProof.ts)
//   · MemberCatalogue — "Everything we publish": three counts and the four
//                       category cards from SITE_PLAN.catalogue
//   · MemberPitch     — the frame: hero band on top, then the catalogue beside
//                       whatever card it is given (the wall's plan picker, or
//                       the account page's card). Desktop two columns, phone
//                       stacked with the card first so the price and the
//                       button come before the long list.
// Shown to someone without the plan: the wall after the free month, and the
// account page during it. A subscriber never sees it (the frame steps aside).

import { useEffect, useState } from "react";

import { useSiteAccess } from "@/hooks/useSiteAccess";
import { barnCountLabel, fetchPaywallProof, type PaywallProof } from "@/lib/memberProof";
import { SITE_PLAN } from "@/lib/sitePlan";

const ICON: Record<string, React.ReactNode> = {
  "Live feeder market": <path d="M3 17l5-6 4 4 8-9M16 6h4v4" />,
  "The Cattle Market Central Indexes": <path d="M4 20V11M10 20V5M16 20v-8M21 20H3" />,
  "Packer, beef & supply": <path d="M3 8l9-4 9 4-9 4-9-4zM3 8v8l9 4 9-4V8M12 12v8" />,
  "Reports, Clara & alerts": <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />,
};

/** One read of the proof, shared by the hero and the catalogue's count. */
export function useMemberProof(): PaywallProof | null {
  const [proof, setProof] = useState<PaywallProof | null>(null);
  useEffect(() => {
    let live = true;
    void fetchPaywallProof().then((p) => { if (live) setProof(p); });
    return () => { live = false; };
  }, []);
  return proof;
}

export function MemberHero({ proof }: { proof: PaywallProof | null }) {
  return (
    <section className="rbpitch-hero" aria-label="Live sale barns">
      <div className="rbpitch-hero-in">
        <div>
          <div className="rbpitch-big">
            <span className="rbpitch-n">{barnCountLabel(proof?.barnCount ?? null)}</span>
            <span className="rbpitch-lbl">Sale barns,<br />live as they sell</span>
          </div>
          <p className="rbpitch-claim">{SITE_PLAN.claim}</p>
          <p className="rbpitch-also">Plus all {SITE_PLAN.indexCount} Cattle Market Central Indexes, the averages, the maps, Clara and every tool. One plan.</p>
        </div>
        {proof && proof.lots.length > 0 && (
          <div className="rbpitch-strip" aria-label="On the board right now">
            {proof.lots.slice(0, 4).map((l) => (
              <div key={`${l.state}|${l.barn}`} className="rbpitch-lot" data-live={l.live ? "1" : undefined}>
                <small>{l.short}, {l.state}</small>
                <b>{l.priceLabel}</b>
                <span>{l.line}{l.live ? " · selling now" : ""}</span>
              </div>
            ))}
            <p className="rbpitch-cap">On the board right now</p>
          </div>
        )}
      </div>
    </section>
  );
}

export function MemberCatalogue({ proof }: { proof: PaywallProof | null }) {
  return (
    <section className="rbpitch-cat" aria-label={`What a ${SITE_PLAN.short} gets`}>
      <h3>What a {SITE_PLAN.short} gets</h3>
      <h2>Everything we publish</h2>
      <div className="rbpitch-nums">
        <div><b>{barnCountLabel(proof?.barnCount ?? null)}</b><span>Barns live</span></div>
        <div><b>{SITE_PLAN.indexCount}</b><span>Indexes</span></div>
        <div><b>{SITE_PLAN.toolCount}</b><span>Tools</span></div>
      </div>
      {SITE_PLAN.catalogue.map((c) => (
        <div key={c.title} className="rbpitch-row">
          <i aria-hidden="true"><svg viewBox="0 0 24 24">{ICON[c.title]}</svg></i>
          <div><b>{c.title}<em>{c.count}</em></b><span>{c.items}</span></div>
        </div>
      ))}
    </section>
  );
}

/** The frame, only for someone without the plan (the free month, or after it):
 *  a subscriber's account page keeps its plain card. Reads the same access the
 *  gate does, so the two cannot disagree about who has a plan. */
export function MemberPitchIfNoPlan({ children }: { children: React.ReactNode }) {
  const { access } = useSiteAccess();
  const noPlan = access.state === "expired" || (access.state === "open" && access.daysLeft !== null);
  return noPlan ? <MemberPitch>{children}</MemberPitch> : <>{children}</>;
}

/** The frame: the hero on top, the catalogue beside the card. (The membership
 *  page itself is its own landing page now, MembershipPage.tsx; this frame
 *  stays for the wall after the free month and the account page.) */
export default function MemberPitch({ children }: { children: React.ReactNode }) {
  const proof = useMemberProof();
  return (
    <>
      <MemberHero proof={proof} />
      <div className="rbpitch-wrap">
        <MemberCatalogue proof={proof} />
        <div className="rbpitch-card">{children}</div>
      </div>
    </>
  );
}
